// src/components/LanguageSwitcher.js
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateUserSettings } from '../store/settingsSlice';

const LanguageSwitcher = ({ mobile = false, onLanguageChange }) => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.session.user);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem("language", lng);
    } catch (_e) {
      // Ignore storage errors (private mode etc.).
    }
    // Persist to the server so this choice sticks across devices and so the
    // App-level settings sync doesn't revert it on the next reload. Only
    // meaningful when the user is signed in.
    if (user) {
      dispatch(updateUserSettings({ defaultLanguage: lng }));
    }
    setDropdownOpen(false);
    if (onLanguageChange) {
      onLanguageChange();
    }
  };

  const currentLanguage = i18n.language || 'en';
  const languageLabel = currentLanguage === 'ko' ? '한국어' : 'English';

  // Use different container classes based on mobile prop
  const containerClasses = mobile ? "relative" : "inline-block";

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className={containerClasses}>
      <div className="relative inline-block text-left ml-2">
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="inline-flex items-center justify-center gap-1 rounded-xl px-4 h-10 bg-white text-sm font-medium text-[#0e1420] shadow-[-2px_-2px_5px_rgba(255,255,255,0.95),3px_3px_8px_rgba(0,0,0,0.14)] hover:shadow-[-1px_-1px_3px_rgba(255,255,255,0.95),2px_2px_5px_rgba(0,0,0,0.12)] active:shadow-[inset_2px_2px_4px_rgba(0,0,0,0.10),inset_-2px_-2px_4px_rgba(255,255,255,0.85)] focus:outline-none whitespace-nowrap transition-all"
        >
          {languageLabel}
          <svg
            className="ml-1 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {dropdownOpen && (
          <div
            className={`origin-${mobile ? 'top-left' : 'top-right'} absolute ${
              mobile ? 'left-full top-0 ml-2' : 'right-0 mt-2'
            } w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 transition-transform duration-300 ${
              mobile ? 'animate-slideInRight' : ''
            } z-50`}
          >
            <div className="py-1">
              <button
                onClick={() => changeLanguage('en')}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                English
              </button>
              <button
                onClick={() => changeLanguage('ko')}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                한국어
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
